/**
 * CKEditor 5 React Component
 *
 * A modern, SEO-friendly rich text editor built on top of CKEditor 5.
 * Supports Vietnamese language, image uploads, and SEO content formatting.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { CKEditor as CKEditorReact } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ICKEditorProps } from './types';
import { getEditorConfig } from './config';
import './styles.css';

const CKEditorComponent = ({
	value = '',
	onChange,
	onReady,
	onBlur,
	onFocus,
	disabled = false,
	placeholder,
	className = '',
	minHeight = 400,
	maxHeight = 800,
	config,
}: ICKEditorProps) => {
	// Memoize editor config to prevent unnecessary re-renders
	const editorConfig = useMemo(() => {
		return getEditorConfig(placeholder, config);
	}, [placeholder, config]);

	// Handle content change
	const handleChange = useCallback(
		(_: unknown, editor: { getData: () => string }) => {
			if (onChange) {
				const data = editor.getData();
				onChange(data);
			}
		},
		[onChange]
	);

	// Adjust toolbar position when editor is ready
	const handleReady = useCallback(
		(editor: unknown) => {
			const toolbar = document.querySelector('.ck-sticky-panel__content') as HTMLElement;
			if (toolbar) {
				toolbar.style.top = '65px';
			}
			if (onReady) {
				onReady(editor);
			}
		},
		[onReady]
	);

	// Handle focus
	const handleFocus = useCallback(
		(event: unknown, editor: unknown) => {
			if (onFocus) {
				onFocus(event, editor);
			}
		},
		[onFocus]
	);

	return (
		<div
			className={`ckeditor-wrapper ${className}`}
			style={{
				minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
				maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
			}}
		>
			<CKEditorReact
				editor={ClassicEditor}
				data={value}
				disabled={disabled}
				onChange={handleChange}
				onReady={handleReady}
				onFocus={handleFocus}
				onBlur={onBlur}
				config={editorConfig}
			/>
		</div>
	);
};

export default CKEditorComponent;
